import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CrimeReference,
  FILE_URL,
  Investigation,
  Reporter,
  Roles,
} from "types";
import { deleteIcon, editIcon, viewIcon } from "utils/constant";
import { constructFileUrl, hasRole } from "utils/helper";
import ReporterDetail from "./modals/ReporterDetail";
import ReporterForm from "./modals/ReporterForm";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident?: CrimeReference;
  canManageIncidentDetail?: boolean;
};

const ReporterTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [reporters, setReporters] = useState<Reporter[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");

  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);

  // const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedReporter, setSelectedReporter] = React.useState<Reporter>();

  useEffect(() => {
    if (incident) {
      setReporters(incident?.reporters || []);
    }
  }, [incident]);

  const handleFormOpen = () => {
    setIsOpenForm(true);
  };
  const handleFormClose = (
    refetch = false,
    reporter: Reporter | null = null,
  ) => {
    setIsOpenForm(false);
    if (refetch && reporter) {
      if (action === "Edit") {
        setReporters(
          reporters.map((r) =>
            r.uu_id === reporter.uu_id ? { ...r, ...reporter } : r,
          ),
        );
      } else {
        setReporters([reporter, ...reporters]);
      }
    }
  };

  const handleDetailOpen = () => {
    setIsOpenDetail(true);
  };
  const handleDetailClose = () => {
    setIsOpenDetail(false);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">👤</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("reporter_information")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_reporters_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <ActionButton
              onClick={() => {
                handleFormOpen();
                setAction("Add");
              }}
            >
              ➕ {t("add_reporter")}
            </ActionButton>
          )}
        </div>
      </div>

      {/* Reporters List */}
      {reporters.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_reporters_found")}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {reporters?.map((reporter, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img
                    src={(() => {
                      const doc = reporter?.documents?.find(
                        (doc) => doc.document_type === "reporter_picture",
                      );
                      return doc
                        ? constructFileUrl(doc, FILE_URL.REPORTER)
                        : require("../../../images/imageVector.png");
                    })()}
                    alt="Reporter"
                    className="w-12 h-12 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => {
                      setSelectedReporter(reporter);
                      setIsImageModalOpen(true);
                    }}
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {`${reporter?.first_name} ${reporter?.middle_name} ${reporter?.last_name}`}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                      <span>
                        {reporter?.age} {t("years_old_label")}
                      </span>
                      <span>•</span>
                      <span>{reporter?.education_level}</span>
                      <span>•</span>
                      <span>{reporter?.nationality}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedReporter(reporter);
                      handleDetailOpen();
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setAction("Edit");
                            setSelectedReporter(reporter);
                            handleFormOpen();
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_reporter")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedReporter(reporter);
                            // setIsOpenDelete(true);
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_reporter")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {incident && (
        <ReporterForm
          action={action}
          incident={incident}
          reporter={action === "Edit" ? selectedReporter : undefined}
          isOpen={isOpenForm}
          onClose={handleFormClose}
        />
      )}
      <ReporterDetail
        reporter={selectedReporter}
        isOpen={isOpenDetail}
        onClose={handleDetailClose}
      />
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={(() => {
          const doc = selectedReporter?.documents?.find(
            (doc) => doc.document_type === "reporter_picture",
          );
          return doc
            ? constructFileUrl(doc, FILE_URL.REPORTER)
            : require("../../../images/imageVector.png");
        })()}
        label={
          selectedReporter?.first_name +
          " " +
          selectedReporter?.middle_name +
          " " +
          selectedReporter?.last_name
        }
        onClose={() => setIsImageModalOpen(false)}
      />
    </div>
  );
};

export default ReporterTab;
